/**
 * Visual-identity overrides for a document contract: PER-ASSET keep / replace / remove
 * for the cover background, each header/footer logo, and each body/inline image -
 * plus add-when-missing for a cover or a header logo. Applied as a first-class step in
 * SOW template authoring (the `prepare_template_visuals` / `save_sow_template` agent
 * flow) AND the admin workbench's Cover & Brand panel - so the rules live in exactly
 * one place and never drift between the two paths.
 *
 * Pure: clones its inputs (the contract is JSON-serializable - image bytes live in
 * the separate asset map, never the contract) and returns NEW objects.
 *
 * The asset model is slot-indirected: the contract references images as
 * `{{protoss:asset:<slot>}}` and a `slot → asset-uuid` map resolves them at export.
 * So a REPLACE only re-points the map (the contract keeps its slot ref - this works
 * uniformly for cover/logo/body), while a REMOVE must edit BOTH halves - delete the
 * contract ref AND drop the slot - or a referenced-but-missing slot renders blank.
 */
/** Stable slots for assets PROVIDED in the visual step (no parsed slot to reuse). */
const PROVIDED_COVER_SLOT = 'provided-cover';
const PROVIDED_LOGO_SLOT = 'provided-logo';
/** Default rendered size (px) for a provided logo inserted into an otherwise-bare header. */
const PROVIDED_LOGO_SIZE = { width: 120, height: 48 };
const ASSET_SLOT_RE = /\{\{protoss:asset:([^}]+)\}\}/;
function slotOf(src) {
    return typeof src === 'string' ? (ASSET_SLOT_RE.exec(src)?.[1] ?? undefined) : undefined;
}
function isImageRun(run) {
    return typeof run === 'object' && run !== null && 'type' in run && run.type === 'image';
}
/** Plain text of a paragraph's text runs (used to label a body image's section). */
function paragraphText(p) {
    return p.children
        .map((r) => (!('type' in r) && typeof r.text === 'string' ? r.text : ''))
        .join('')
        .trim();
}
/** Every defined header part (default/first/even) across a section. */
function headerParts(section) {
    const p = section.properties;
    return [p?.headers?.default, p?.headers?.first, p?.headers?.even].filter((hf) => Boolean(hf));
}
/** Every defined footer part (default/first/even) across a section. */
function footerParts(section) {
    const p = section.properties;
    return [p?.footers?.default, p?.footers?.first, p?.footers?.even].filter((hf) => Boolean(hf));
}
/** Every defined header/footer part across a section. */
function headerFooterParts(section) {
    return [...headerParts(section), ...footerParts(section)];
}
/** Visit each image-run slot in a children tree (recursing table cells). */
function walkImageSlots(nodes, visit) {
    for (const node of nodes) {
        if (node.type === 'paragraph') {
            for (const run of node.children) {
                if (isImageRun(run)) {
                    const slot = slotOf(run.src);
                    if (slot)
                        visit(slot);
                }
            }
        }
        else if (node.type === 'table') {
            for (const row of node.rows)
                for (const cell of row.children)
                    walkImageSlots(cell.children, visit);
        }
    }
}
/** Body/inline image slots in a section, each tagged with its nearest preceding heading. */
function collectBodyImages(section) {
    const out = [];
    let lastHeading;
    const walk = (nodes) => {
        for (const node of nodes) {
            if (node.type === 'paragraph') {
                if (node.heading) {
                    const t = paragraphText(node);
                    if (t)
                        lastHeading = t;
                }
                for (const run of node.children) {
                    if (isImageRun(run)) {
                        const slot = slotOf(run.src);
                        if (slot)
                            out.push({ slot, ...(lastHeading ? { sectionHeading: lastHeading } : {}) });
                    }
                }
            }
            else if (node.type === 'table') {
                for (const row of node.rows)
                    for (const cell of row.children)
                        walk(cell.children);
            }
        }
    };
    walk(section.children);
    return out;
}
/**
 * Every visual asset a contract references, located: the cover background, each
 * header/footer logo, and each body/inline image. Deduped by slot (first wins).
 * `coverSlots`/`logoSlots` are kept as flat convenience lists. Shared by
 * `applyVisualOverrides` consumers and anything displaying the current assets (the
 * admin Cover & Brand panel) without re-deriving the rules.
 */
export function collectVisualAssetSlots(contract) {
    const coverSlots = [];
    const logoSlots = [];
    const located = [];
    const seen = new Set();
    const add = (entry) => {
        if (seen.has(entry.slot))
            return;
        seen.add(entry.slot);
        located.push(entry);
        if (entry.location === 'cover')
            coverSlots.push(entry.slot);
        else if (entry.location === 'header' || entry.location === 'footer')
            logoSlots.push(entry.slot);
    };
    for (const section of contract.sections) {
        const cover = slotOf(section.properties?.coverBackground);
        if (cover)
            add({ slot: cover, location: 'cover' });
        for (const hf of headerParts(section))
            walkImageSlots(hf.children, (slot) => add({ slot, location: 'header' }));
        for (const hf of footerParts(section))
            walkImageSlots(hf.children, (slot) => add({ slot, location: 'footer' }));
        for (const { slot, sectionHeading } of collectBodyImages(section)) {
            add({ slot, location: 'body', ...(sectionHeading ? { sectionHeading } : {}) });
        }
    }
    return { coverSlots, logoSlots, located };
}
/**
 * Provide/replace the cover with `uuid`. Reuses an existing cover slot when one is present
 * (re-points the map, leaving the contract ref intact); otherwise sets a fresh slot on the
 * first (cover/title) section. Mutates `contract`/`assetSlots` in place.
 */
function setCover(contract, assetSlots, uuid) {
    for (const section of contract.sections) {
        const slot = slotOf(section.properties?.coverBackground);
        if (slot) {
            assetSlots[slot] = uuid; // re-point an existing cover
            return;
        }
    }
    const first = contract.sections[0];
    if (!first)
        return;
    first.properties = first.properties ?? {};
    first.properties.coverBackground = `{{protoss:asset:${PROVIDED_COVER_SLOT}}}`;
    assetSlots[PROVIDED_COVER_SLOT] = uuid;
}
/**
 * Insert a logo referencing `uuid` into the first section's default header (creating the
 * header part when absent). Used only when no header/footer logo already exists. Mutates
 * in place.
 */
function insertHeaderLogo(contract, assetSlots, uuid) {
    const first = contract.sections[0];
    if (!first)
        return;
    first.properties = first.properties ?? {};
    first.properties.headers = first.properties.headers ?? {};
    const header = first.properties.headers.default ?? { children: [] };
    const image = {
        type: 'image',
        src: `{{protoss:asset:${PROVIDED_LOGO_SLOT}}}`,
        width: PROVIDED_LOGO_SIZE.width,
        height: PROVIDED_LOGO_SIZE.height,
    };
    const logoParagraph = { type: 'paragraph', children: [image] };
    header.children = [logoParagraph, ...header.children];
    first.properties.headers.default = header;
    assetSlots[PROVIDED_LOGO_SLOT] = uuid;
}
/** Remove image runs whose slot is in `slots` from a children tree (in place). */
function dropImageRuns(nodes, slots) {
    for (const node of nodes) {
        if (node.type === 'paragraph') {
            node.children = node.children.filter((run) => {
                if (!isImageRun(run))
                    return true;
                const slot = slotOf(run.src);
                return !(slot !== undefined && slots.has(slot));
            });
        }
        else if (node.type === 'table') {
            for (const row of node.rows)
                for (const cell of row.children)
                    dropImageRuns(cell.children, slots);
        }
    }
}
/**
 * Remove one asset everywhere it appears - cover background property OR an image run in
 * the body / a header / a footer / a table cell - AND drop its slot from the map. Editing
 * both halves prevents a referenced-but-missing slot rendering blank. Mutates in place.
 */
function removeAssetSlot(contract, assetSlots, slot) {
    const target = new Set([slot]);
    for (const section of contract.sections) {
        if (section.properties?.coverBackground && slotOf(section.properties.coverBackground) === slot) {
            delete section.properties.coverBackground;
        }
        dropImageRuns(section.children, target);
        for (const hf of headerFooterParts(section))
            dropImageRuns(hf.children, target);
    }
    delete assetSlots[slot];
}
/**
 * Apply per-asset visual-identity overrides to a contract + its slot→uuid map. Returns
 * new objects (inputs untouched). `workspaceLogoUuid` is required only for a
 * `'workspace'` action / `addLogoWorkspace`; absent, those are no-ops (nothing to
 * point at).
 */
export function applyVisualOverrides(contractIn, assetSlotsIn, overrides, workspaceLogoUuid) {
    const contract = JSON.parse(JSON.stringify(contractIn));
    const assetSlots = { ...assetSlotsIn };
    if (!overrides)
        return { contract, assetSlots };
    // Per-slot decisions for existing assets.
    for (const o of overrides.assets ?? []) {
        if (!o.slot || o.action === 'keep')
            continue;
        if (o.action === 'remove') {
            removeAssetSlot(contract, assetSlots, o.slot);
            continue;
        }
        // 'replace' (uploaded uuid) or 'workspace' (brand logo) → re-point the slot; the
        // contract keeps its {{protoss:asset:slot}} ref so the new image renders in place.
        const uuid = o.action === 'workspace' ? (workspaceLogoUuid ?? undefined) : o.assetUuid;
        if (uuid)
            assetSlots[o.slot] = uuid;
    }
    // Add-when-missing (no existing slot to re-point).
    if (overrides.addCoverAssetUuid)
        setCover(contract, assetSlots, overrides.addCoverAssetUuid);
    const addLogoUuid = overrides.addLogoWorkspace
        ? (workspaceLogoUuid ?? undefined)
        : overrides.addLogoAssetUuid;
    if (addLogoUuid)
        insertHeaderLogo(contract, assetSlots, addLogoUuid);
    return { contract, assetSlots };
}
//# sourceMappingURL=visual-overrides.js.map